"use client";

import type { ImageFieldValue, LinkTarget } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { translateCategoryLabel } from "../../i18n/categories";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

export type BlogArticle = {
  category: string;
  title: string;
  excerpt: string;
  author: string;
  authorRole: string;
  date: string;
  href: LinkTarget;
  imageSrc?: ImageFieldValue;
};

/** Blog article card with orange tag, author and date. */
export function BlogArticleCard({ category, title, excerpt, author, authorRole, date, href, imageSrc = "/media/d47f9f5af24fd7129274afad903571dd3819a330.png" }: BlogArticle) {
  const locale = useLocale();
  const { src, options } = resolveImageValue(imageSrc);
  const resolvedSrc = src || "/media/d47f9f5af24fd7129274afad903571dd3819a330.png";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const to = resolveLinkHref(href) ?? "/blog";
  return (
    <article className="flex h-full flex-col bg-white">
      <RuntimeLink href={to} className="block bg-slate-100">
        <RuntimeImage
          src={resolvedSrc}
          alt={t(locale, "articleImageAlt")}
          className={mergeClassNames("h-44 w-full object-cover", overrideProps.className)}
          style={mergeStyles(objectPositionStyle, overrideProps.style)}
        />
      </RuntimeLink>
      <div className="flex flex-1 flex-col bg-[#EEF1F0]">
        <div className="flex flex-1 flex-col p-4">
          {/* Tag: compact, orange */}
          <span className="inline-flex w-fit items-center rounded bg-[#FFC105] px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-white">{translateCategoryLabel(locale, category)}</span>
          <RuntimeLink href={to} className="mt-2 block text-[18px] xl:text-[20px] md:text-[20px] font-semibold leading-6 text-[#00B862]">
            {title}
          </RuntimeLink>
          <p className="mt-2 line-clamp-3 text-base xl:text-lg leading-5 text-slate-700">{excerpt}</p>

          {/* Bottom block pinned */}
          <div className="mt-auto pt-4">
            <div className="flex items-center gap-3 text-xs xl:text-sm text-slate-600">
              <RuntimeImage src={resolvedSrc} alt="Auteur" className="h-10 w-10 rounded-full object-cover" />
              <div className="min-w-0 flex min-h-[40px] flex-col justify-center gap-0.5">
                <div className="truncate"><span className="font-semibold text-[#00B862]">{author}</span> <span className="text-slate-500">{authorRole}</span></div>
                <div className="text-xs xl:text-sm text-slate-600">{date}</div>
              </div>
              <RuntimeLink href={to} aria-label={t(locale, "readArticle")} className="ml-auto grid h-8 w-8 place-items-center rounded-full border border-[#00B862] text-[#00B862]">
                {/* simple chevron using › to avoid bringing extra icons */}
                <span className="leading-none text-[18px] xl:text-[20px]">›</span>
              </RuntimeLink>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}


